/**
 * DOS518 - Digital Operations System for Wildfire Management
 * 
 * Copyright (c) 2024-2025 Angelo Irtilaç
 * All rights reserved.
 * 
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, modification, or use is strictly prohibited.
 * 
 * ---
 * 
 * Author: Angelo Irtilaç
 * Qualifications: DOS (Direttore delle Operazioni di Spegnimento)
 *                39 years - Corpo Nazionale dei Vigili del Fuoco
 *                DOS Trainer & Instructor
 * 
 * Location: Carpi, Italy
 * Contact: [inserire email]
 * 
 * ---
 * 
 * This software implements official Italian Fire Department protocols:
 * - C.O.A.U. (Centro Operativo Aereo Unificato)
 * - SI.TA.C. (Sistema Tattico di Coordinamento)
 * - L.A.C.E.S. (Lookout, Awareness, Communication, Escape routes, Safety zones)
 * 
 * Developed with operational expertise acquired through decades of wildfire
 * emergency management and coordination of suppression operations.
 * 
 * For commercial licensing inquiries, please contact the author.
 */
import React, { useState } from 'react';
import './LACES.css';
import { LookutsManagement } from './LookutsManagement';

type LACESSection = 'lookouts' | 'anchor' | 'communications' | 'escape' | 'safety';

export const LACES: React.FC = () => {
  const [activeSection, setActiveSection] = useState<LACESSection | null>(null);

  const toggleSection = (section: LACESSection) => {
    setActiveSection(activeSection === section ? null : section);
  };

  return (
    <div className="laces-container">
      <h2 className="laces-title">🛡️ Protocollo L.A.C.E.S.</h2>

      {/* Grid delle 5 Componenti LACES */}
      <div className="laces-grid">
        <div 
          className={`laces-card laces-lookouts ${activeSection === 'lookouts' ? 'active' : ''}`}
          onClick={() => toggleSection('lookouts')}
        >
          <div className="laces-icon">👁️</div>
          <h3>L - Lookouts</h3>
          <p><strong>Vedette</strong></p>
        </div>

        <div 
          className={`laces-card laces-anchor ${activeSection === 'anchor' ? 'active' : ''}`}
          onClick={() => toggleSection('anchor')}
        >
          <div className="laces-icon">⚓</div>
          <h3>A - Anchor Points</h3>
          <p><strong>Punti di Ancoraggio</strong></p>
        </div>

        <div 
          className={`laces-card laces-communications ${activeSection === 'communications' ? 'active' : ''}`}
          onClick={() => toggleSection('communications')}
        >
          <div className="laces-icon">📡</div>
          <h3>C - Communications</h3>
          <p><strong>Comunicazioni</strong></p>
        </div>

        <div 
          className={`laces-card laces-escape ${activeSection === 'escape' ? 'active' : ''}`}
          onClick={() => toggleSection('escape')}
        >
          <div className="laces-icon">🚪</div>
          <h3>E - Escape Routes</h3>
          <p><strong>Vie di Fuga</strong></p>
        </div>

        <div 
          className={`laces-card laces-safety ${activeSection === 'safety' ? 'active' : ''}`}
          onClick={() => toggleSection('safety')}
        >
          <div className="laces-icon">🏠</div>
          <h3>S - Safety Zones</h3>
          <p><strong>Zone di Sicurezza</strong></p>
        </div>
      </div>

      {/* Pannelli di Dettaglio */}
      <div className="laces-details-container">
        {/* LOOKOUTS */}
        {activeSection === 'lookouts' && (
          <div className="laces-detail-panel">
            <h3>👁️ L - LOOKOUTS (Vedette)</h3>
            
            {/* Gestione Vedette */}
            <LookutsManagement />
            
            <hr className="laces-separator" />
            
            {/* Pannello Informativo */}
            <div className="laces-detail-content">
              <div className="laces-lookout-main">
                <h4>🔍 Avvistamento e Monitoraggio in Corso di Evento</h4>
                
                <div className="laces-monitoring-grid">
                  <div className="laces-monitor-card">
                    <h5>📏 Dimensioni - Fumo</h5>
                    <p>Valutazione estensione area interessata e sviluppo colonna fumogena</p>
                  </div>
                  
                  <div className="laces-monitor-card">
                    <h5>⏱️ Orario - Durata - Fumo</h5>
                    <p>Monitoraggio temporale evoluzione e caratteristiche fumo</p>
                  </div>
                  
                  <div className="laces-monitor-card">
                    <h5>🌤️ Ubicazione: Meteo Locale - Vegetazione</h5>
                    <p>Analisi condizioni meteorologiche e tipo combustibile presente</p>
                  </div>
                  
                  <div className="laces-monitor-card">
                    <h5>📡 Comunicazioni</h5>
                    <p>Mantenimento contatto continuo con comando e squadre operative</p>
                  </div>
                  
                  <div className="laces-monitor-card">
                    <h5>⛰️ Ubicazione: Morfologia - Pendenza</h5>
                    <p>Valutazione influenza terreno su propagazione incendio</p>
                  </div>
                </div>
              </div>

              <div className="laces-lookout-resources">
                <h4>👥 Risorse per Vedette</h4>
                <div className="laces-lookout-grid">
                  <div className="laces-lookout-card">
                    <h5>🚁 VOLONTARIATO AIB</h5>
                    <p>Personale addestrato posizionato strategicamente sul territorio</p>
                  </div>
                  
                  <div className="laces-lookout-card">
                    <h5>🛸 NUCLEO SAPR</h5>
                    <p>Sistemi Aeromobili a Pilotaggio Remoto per sorveglianza</p>
                  </div>
                  
                  <div className="laces-lookout-card">
                    <h5>🌡️ CON TERMOCAMERA</h5>
                    <p>Rilevamento termico focolai e punti caldi</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ANCHOR POINTS */}
        {activeSection === 'anchor' && (
          <div className="laces-detail-panel">
            <h3>⚓ A - ANCHOR POINTS (Punti di Ancoraggio)</h3>
            
            <div className="laces-detail-content">
              <div className="laces-anchor-main">
                <h4>🎯 Ubicazione: Pianificazione della Sicurezza nell'Intervento AIB</h4>
                <p className="laces-anchor-description">
                  I punti di ancoraggio sono elementi fondamentali per garantire un intervento sicuro ed efficace.
                  La loro individuazione deve avvenire durante la fase di pianificazione.
                </p>
              </div>

              <div className="laces-anchor-factors">
                <h4>📋 Fattori da Considerare</h4>
                
                <div className="laces-anchor-factor-grid">
                  <div className="laces-anchor-factor">
                    <div className="laces-factor-icon">⛰️</div>
                    <h5>Ubicazione: Morfologia - Pendenza</h5>
                    <p>Analisi della conformazione del terreno e delle pendenze per individuare punti strategicamente sicuri</p>
                    <ul>
                      <li>Creste e dossi naturali</li>
                      <li>Zone pianeggianti</li>
                      <li>Aree con pendenza favorevole</li>
                    </ul>
                  </div>

                  <div className="laces-anchor-factor">
                    <div className="laces-factor-icon">🌲</div>
                    <h5>Ubicazione: Vegetazione</h5>
                    <p>Valutazione del tipo e della densità della vegetazione presente</p>
                    <ul>
                      <li>Zone con basso carico combustibile</li>
                      <li>Discontinuità vegetazionale</li>
                      <li>Aree con vegetazione ignifuga</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="laces-anchor-types">
                <h4>🏞️ Tipologie di Punti di Ancoraggio</h4>
                <div className="laces-anchor-type-grid">
                  <div className="laces-anchor-type-card">
                    <strong>Barriere Naturali</strong>
                    <p>Corsi d'acqua, zone rocciose, aree bruciate</p>
                  </div>
                  <div className="laces-anchor-type-card">
                    <strong>Barriere Artificiali</strong>
                    <p>Strade, linee tagliafuoco, infrastrutture</p>
                  </div>
                  <div className="laces-anchor-type-card">
                    <strong>Zone Sicure</strong>
                    <p>Aree con scarsa vegetazione o vegetazione verde</p>
                  </div>
                </div>
              </div>

              <div className="laces-strategy-box">
                <h4>🎯 Strategia di Utilizzo</h4>
                <p>I punti di ancoraggio devono essere utilizzati come base sicura da cui iniziare le operazioni, 
                procedendo sistematicamente verso zone progressivamente meno sicure, mantenendo sempre 
                la possibilità di ripiegamento.</p>
              </div>
            </div>
          </div>
        )}

        {/* COMMUNICATIONS */}
        {activeSection === 'communications' && (
          <div className="laces-detail-panel">
            <h3>📡 C - COMMUNICATIONS (Comunicazioni)</h3>
            
            <div className="laces-detail-content">
              <div className="laces-comm-main">
                <h4>📻 Contatto Continuo DTS / ROS / DOS / CS</h4>
                <p className="laces-comm-description">
                  Le comunicazioni devono essere mantenute costantemente tra tutti i livelli della catena di comando 
                  per garantire coordinamento efficace e sicurezza degli operatori.
                </p>
              </div>

              <div className="laces-comm-aspects">
                <h4>🎯 Aspetti Critici delle Comunicazioni</h4>
                
                <div className="laces-comm-aspect-grid">
                  <div className="laces-comm-aspect">
                    <h5>✅ Ricezione del Messaggio - Ubicazione</h5>
                    <p>Conferma ricezione e comunicazione posizione GPS delle squadre</p>
                  </div>

                  <div className="laces-comm-aspect">
                    <h5>⚠️ Assenza di Comunicazione</h5>
                    <p>Protocolli di emergenza in caso di perdita contatto radio</p>
                  </div>

                  <div className="laces-comm-aspect">
                    <h5>🔥 Ubicazione: Posizione Squadra Rispetto al Fronte del Fuoco</h5>
                    <p>Costante aggiornamento posizione relativa delle squadre rispetto all'evoluzione incendio</p>
                  </div>

                  <div className="laces-comm-aspect">
                    <h5>📊 Evoluzione Incendio</h5>
                    <p>Comunicazione continua cambiamenti comportamento fuoco</p>
                  </div>

                  <div className="laces-comm-aspect">
                    <h5>⛰️ Ubicazione: Morfologia - Pendenza</h5>
                    <p>Informazioni su conformazione terreno e difficoltà operative</p>
                  </div>
                </div>
              </div>

              <div className="laces-comm-risks">
                <h4>⚡ Rischi da Comunicare Immediatamente</h4>
                
                <div className="laces-risk-grid">
                  <div className="laces-risk-card laces-risk-ground">
                    <h5>⚡ Rischio da Elettricità per Operatori a Terra</h5>
                    <ul>
                      <li>Presenza di elettrodotti</li>
                      <li>Cavi elettrici abbattuti</li>
                      <li>Rischio folgorazione</li>
                      <li>Zone da evacuare</li>
                    </ul>
                  </div>

                  <div className="laces-risk-card laces-risk-air">
                    <h5>⚡ Rischio da Elettricità per Operatori a Terra e Piloti Velivoli</h5>
                    <ul>
                      <li>Coordinamento mezzi aerei</li>
                      <li>Altezza elettrodotti</li>
                      <li>Zone interdette al volo</li>
                      <li>Comunicazione con piloti</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="laces-comm-protocols">
                <h4>📋 Protocolli di Comunicazione</h4>
                <ul className="laces-protocol-list">
                  <li>✓ Check radio periodici obbligatori</li>
                  <li>✓ Conferma ricezione messaggi critici</li>
                  <li>✓ Comunicazione immediata situazioni di pericolo</li>
                  <li>✓ Aggiornamenti posizione ogni 15-30 minuti</li>
                  <li>✓ Canali radio dedicati e procedure standardizzate</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* ESCAPE ROUTES */}
        {activeSection === 'escape' && (
          <div className="laces-detail-panel">
            <h3>🚪 E - ESCAPE ROUTES (Vie di Fuga)</h3>
            
            <div className="laces-detail-content">
              <div className="laces-escape-main">
                <h4>🎯 Ubicazione: Pianificazione della Sicurezza nell'Intervento AIB (I.C.S.)</h4>
                <p className="laces-escape-description">
                  Le vie di fuga devono essere identificate e pianificate prima dell'inizio delle operazioni, 
                  nell'ambito del sistema di comando incidente (I.C.S.).
                </p>
              </div>

              <div className="laces-escape-factors">
                <h4>📋 Fattori Critici per Vie di Fuga</h4>
                
                <div className="laces-escape-factor-grid">
                  <div className="laces-escape-factor">
                    <div className="laces-factor-icon">⛰️</div>
                    <h5>Ubicazione: Morfologia - Pendenza</h5>
                    <p>Le vie di fuga devono considerare:</p>
                    <ul>
                      <li>Pendenze del terreno (evitare salite ripide)</li>
                      <li>Conformazione morfologica</li>
                      <li>Presenza di ostacoli naturali</li>
                      <li>Percorribilità in condizioni di stress</li>
                    </ul>
                  </div>

                  <div className="laces-escape-factor">
                    <div className="laces-factor-icon">🌲</div>
                    <h5>Ubicazione: Vegetazione</h5>
                    <p>Valutazione della vegetazione lungo il percorso:</p>
                    <ul>
                      <li>Tipo e densità vegetazione</li>
                      <li>Carico combustibile presente</li>
                      <li>Possibilità di propagazione fuoco</li>
                      <li>Ostacoli vegetazionali</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="laces-escape-requirements">
                <h4>✅ Requisiti Fondamentali</h4>
                <div className="laces-req-grid">
                  <div className="laces-req-item">
                    <strong>👣 Identificazione Preventiva</strong>
                    <p>Le vie devono essere riconosciute fisicamente sul terreno, non solo su carta</p>
                  </div>
                  <div className="laces-req-item">
                    <strong>💨 Percorribili con Fumo</strong>
                    <p>Utilizzabili anche in condizioni di scarsa visibilità</p>
                  </div>
                  <div className="laces-req-item">
                    <strong>🌲 Libere da Combustibili</strong>
                    <p>Percorsi con minimo carico combustibile</p>
                  </div>
                  <div className="laces-req-item">
                    <strong>⏱️ Tempo Sufficiente</strong>
                    <p>Garantire tempo adeguato per raggiungere zona sicura</p>
                  </div>
                </div>
              </div>

              <div className="laces-warning-box">
                <h4>⚠️ Attenzione</h4>
                <p>Le vie di fuga devono essere comunicate chiaramente a tutto il personale operativo e 
                verificate periodicamente durante le operazioni. In caso di cambiamento condizioni, 
                devono essere identificate nuove vie di fuga alternative.</p>
              </div>
            </div>
          </div>
        )}

        {/* SAFETY ZONES */}
        {activeSection === 'safety' && (
          <div className="laces-detail-panel">
            <h3>🏠 S - SAFETY ZONES (Zone di Sicurezza)</h3>
            
            <div className="laces-detail-content">
              <div className="laces-safety-main">
                <h4>🎯 Definizione e Caratteristiche</h4>
                <p className="laces-safety-description">
                  Le zone di sicurezza sono aree dove il personale può rifugiarsi in caso di emergenza, 
                  protette dall'avanzamento del fuoco e dalle sue conseguenze dirette.
                </p>
              </div>

              <div className="laces-safety-characteristics">
                <h4>✅ Caratteristiche delle Zone di Sicurezza</h4>
                
                <div className="laces-safety-char-grid">
                  <div className="laces-safety-char">
                    <div className="laces-char-icon">🌿</div>
                    <h5>Vegetazione Scarsa o Assente</h5>
                    <p>Zone con minimo carico combustibile: aree bruciate, rocciose, prati rasi</p>
                  </div>

                  <div className="laces-safety-char">
                    <div className="laces-char-icon">📏</div>
                    <h5>Dimensioni Adeguate</h5>
                    <p>Spazio sufficiente per tutto il personale, considerando calore radiante del fuoco</p>
                  </div>

                  <div className="laces-safety-char">
                    <div className="laces-char-icon">🚶</div>
                    <h5>Facilmente Raggiungibile</h5>
                    <p>Accessibile in tempi brevi da tutti i punti operativi</p>
                  </div>

                  <div className="laces-safety-char">
                    <div className="laces-char-icon">💨</div>
                    <h5>Protetta da Vento</h5>
                    <p>Posizionata considerando direzione vento dominante</p>
                  </div>
                </div>
              </div>

              <div className="laces-safety-examples">
                <h4>🏞️ Esempi di Zone di Sicurezza</h4>
                <div className="laces-safety-example-grid">
                  <div className="laces-safety-example">
                    <strong>Aree Bruciate Recenti</strong>
                    <p>Zone dove l'incendio è già passato e il combustibile è consumato</p>
                  </div>
                  <div className="laces-safety-example">
                    <strong>Corpi Idrici</strong>
                    <p>Laghi, fiumi, invasi artificiali con sponde accessibili</p>
                  </div>
                  <div className="laces-safety-example">
                    <strong>Aree Urbanizzate</strong>
                    <p>Piazzali, parcheggi, aree industriali prive di vegetazione</p>
                  </div>
                  <div className="laces-safety-example">
                    <strong>Zone Rocciose</strong>
                    <p>Affioramenti rocciosi privi di vegetazione combustibile</p>
                  </div>
                </div>
              </div>

              <div className="laces-safety-protocols">
                <h4>📋 Protocolli di Sicurezza</h4>
                <ul className="laces-safety-protocol-list">
                  <li>✓ Identificare almeno 2 zone di sicurezza per ogni area operativa</li>
                  <li>✓ Comunicare posizione zone sicure a tutto il personale</li>
                  <li>✓ Verificare accessibilità continua durante operazioni</li>
                  <li>✓ Stabilire procedure di evacuazione chiare</li>
                  <li>✓ Monitorare condizioni meteo che potrebbero compromettere sicurezza zone</li>
                </ul>
              </div>

              <div className="laces-critical-box">
                <h4>🚨 Regola Critica</h4>
                <p><strong>La distanza dalla zona di sicurezza non deve mai superare i 4 minuti di percorrenza a piedi.</strong></p>
                <p>Questo tempo deve essere calcolato considerando terreno difficile, equipaggiamento pesante e condizioni di stress.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
